import * as THREE from 'three';

// createRenderLoop: returns { start(), stop() }
export function createRenderLoop({ renderer, scene, camera, controls, labelRenderer, depthPass, sensors, logger }) {
	let rafId = null;

	// temporaries reused every frame
	const tmpWp = new THREE.Vector3();
	const tmpNdc = new THREE.Vector3();
	const tmpNdc2 = new THREE.Vector3();
	const tmpViewDir = new THREE.Vector3();
	const tmpCamUp = new THREE.Vector3();
	const tmpRight = new THREE.Vector3();
	const tmpVec = new THREE.Vector3();
	const tmpVec2 = new THREE.Vector3();

	function render() {
		if (controls && typeof controls.update === 'function') controls.update();

		if (camera && typeof camera.updateMatrixWorld === 'function') camera.updateMatrixWorld(true);
		if (camera && typeof camera.updateProjectionMatrix === 'function') camera.updateProjectionMatrix();

		const w = renderer.domElement.clientWidth || window.innerWidth;
		const h = renderer.domElement.clientHeight || window.innerHeight;
		const pr = renderer.getPixelRatio ? renderer.getPixelRatio() : (window.devicePixelRatio || 1);
		const dw = Math.floor(w * pr), dh = Math.floor(h * pr);

		const depthTex = depthPass && typeof depthPass.renderDepth === 'function'
			? depthPass.renderDepth(scene, camera, { cssWidth: w, cssHeight: h, hideObjects: [sensors] })
			: null;

		if (depthTex) {
			sensors.traverse(obj => {
				if (!(obj.isMesh && obj.material && obj.material.uniforms && obj.material.uniforms.tDepth)) return;
				const uniforms = obj.material.uniforms;
				uniforms.tDepth.value = depthTex;
				uniforms.cameraNear.value = camera.near;
				uniforms.cameraFar.value = camera.far;
				if (uniforms.resolution) uniforms.resolution.value.set(dw, dh);

				if (uniforms.uCenter && uniforms.uProjRadius) {
					obj.getWorldPosition(tmpWp);
					tmpNdc.copy(tmpWp).project(camera);
					const cx = (tmpNdc.x * 0.5 + 0.5) * dw;
					const cy = (-tmpNdc.y * 0.5 + 0.5) * dh;

					let worldRadius = 1.0;
					try {
						if (obj.geometry && obj.geometry.boundingSphere) worldRadius = obj.geometry.boundingSphere.radius;
						else if (obj.geometry && typeof obj.geometry.computeBoundingSphere === 'function') { obj.geometry.computeBoundingSphere(); worldRadius = obj.geometry.boundingSphere ? obj.geometry.boundingSphere.radius : 1.0; }
						if (obj.userData && typeof obj.userData.worldRadius === 'number') worldRadius = obj.userData.worldRadius;
					} catch (e) { worldRadius = 1.0; }

					camera.getWorldDirection(tmpViewDir);
					tmpCamUp.copy(camera.up || new THREE.Vector3(0, 0, 1));
					tmpRight.crossVectors(tmpViewDir, tmpCamUp).normalize();
					if (tmpRight.lengthSq() < 1e-6) tmpRight.set(1, 0, 0);

					tmpVec.copy(tmpRight).multiplyScalar(worldRadius);
					const rightOff = tmpVec2.copy(tmpWp).add(tmpVec);
					tmpNdc2.copy(rightOff).project(camera);
					const cxOffR = (tmpNdc2.x * 0.5 + 0.5) * dw;
					const cyOffR = (-tmpNdc2.y * 0.5 + 0.5) * dh;
					const radiusR = Math.hypot(cxOffR - cx, cyOffR - cy);

					tmpVec.copy(tmpCamUp).normalize().multiplyScalar(worldRadius);
					const upOff = tmpVec2.copy(tmpWp).add(tmpVec);
					tmpNdc2.copy(upOff).project(camera);
					const cxOffU = (tmpNdc2.x * 0.5 + 0.5) * dw;
					const cyOffU = (-tmpNdc2.y * 0.5 + 0.5) * dh;
					const radiusU = Math.hypot(cxOffU - cx, cyOffU - cy);

					const projRadius = (radiusR + radiusU) * 0.5;
					uniforms.uProjRadius.value = projRadius;
					uniforms.uCenter.value.set(cx, cy);
				}
			});
		}

		try {
			renderer.render(scene, camera);
			if (labelRenderer && typeof labelRenderer.render === 'function') labelRenderer.render(scene, camera);
		} catch (e) {
			if (logger && logger.error) logger.error('renderLoop render failed', e);
		}

		rafId = requestAnimationFrame(render);
	}

	function start() { if (!rafId) rafId = requestAnimationFrame(render); }
	function stop() { if (rafId) { cancelAnimationFrame(rafId); rafId = null; } }

	return { start, stop };
}

export default createRenderLoop;
